import * as vp from 'vopay';
import { FunctionComponent, JSX, useEffect } from 'react';
import moment from 'moment';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import usePostTransactionsHistory from '../../../../hooks/useApi/useFetchTransactionsHistory';

const { Table, Box, SubTitle, Badge, formattedCurrency } = vp;

type TransactionInfoProps = {
  transaction: vp.EftTransactionResponse & {
    ParentTransaction?: vp.LinkedTransactionsMap;
    SubTransactions?: vp.LinkedTransactionsMap;
  };
};

type DataProps = {
  transactionId: string;
  hasErrorInfo: boolean;
  failureReason: string;
};

type FC = FunctionComponent<TransactionInfoProps>;

type KeyValuePair = {
  display: string;
  value: string | JSX.Element;
  visible?: boolean;
};

type TablePropsHandlers = {
  transactionDetail: () => vp.TableProps;
  accountHolderDetails: () => vp.TableProps;
  bankAccountDetails: () => vp.TableProps;
  errorInfo: () => vp.TableProps;
};

const RefundTransactionInfo: FC = (props: TransactionInfoProps) => {
  const { transaction } = props;
  const { state } = useLocation<DataProps>();
  const currentDate = moment().utc().format('YYYY-MM-DD');
  const [transactionsHistoryResponse, getTransactionsHistory] = usePostTransactionsHistory({});
  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.RefundTransactionInfo',
  });
  const { t: tGlobal } = useTranslation('translations');
  const transactionHistory = Object.values(transactionsHistoryResponse.Transactions);

  useEffect(() => {
    getTransactionsHistory({
      StartDateTime: moment().subtract(5, 'years').format('YYYY-MM-DD'),
      EndDateTime: currentDate,
      TransactionID: transaction.TransactionID,
    });
  }, [transaction.TransactionID, currentDate]);

  return (
    <>
      <Box mt="lg" mb="md">
        <SubTitle subTitle={t('transactionDetailsHeader') || ''} />
      </Box>
      <Table {...getTableProps('transactionDetail')} />
      <Box mt="lg" mb="md">
        <SubTitle subTitle={t('accountHolderDetailsHeader') || ''} />
      </Box>
      <Table {...getTableProps('accountHolderDetails')} />
      <Box mt="lg" mb="md">
        <SubTitle subTitle={t('bankAccountDetailsHeader') || ''} />
      </Box>
      <Table {...getTableProps('bankAccountDetails')} />
      {state?.hasErrorInfo && (
        <>
          <Box mt="lg" pt="md" mb="md">
            <SubTitle subTitle={t('errorInfoHeader') || ''} />
          </Box>
          <Table {...getTableProps('errorInfo')} />
        </>
      )}
    </>
  );

  function getTableProps(handler: keyof TablePropsHandlers): vp.TableProps {
    const handlers: TablePropsHandlers = {
      transactionDetail: () => {
        const transactionDetailsPairs: KeyValuePair[] = [
          {
            display: t('transactionDetails.transactionID'),
            value: transaction.TransactionID,
            visible: true,
          },
          {
            display: t('transactionDetails.transactionStatus'),
            value: (
              <Badge
                label={vp.capitalizeFirstLetter(
                  tGlobal(`Transaction.TransactionHistory.statuses.${transaction.TransactionStatus}`, {
                    defaultValue: transaction.TransactionStatus,
                  }),
                )}
                hasIcon
                color={vp.GetColorStatus(transaction.TransactionStatus)}
              />
            ),
            visible: true,
          },
          {
            display: t('transactionDetails.transactionDate'),
            value: transaction.TransactionDateTime,
            visible: true,
          },
          {
            display: t('transactionDetails.lastModified'),
            value: transaction.LastModified,
            visible: true,
          },
          {
            display: t('transactionDetails.type'),
            value: tGlobal('Transaction.TransactionHistory.transactionTypes.refund', { defaultValue: 'Refund' }) || '',
            visible: true,
          },
          {
            display: t('transactionDetails.amount'),
            value: `$${formattedCurrency(transaction.Amount)}`,
            visible: true,
          },
          {
            display: t('transactionDetails.holdAmount'),
            value: `$${formattedCurrency(transaction.HoldAmount)}`,
            visible: true,
          },
          {
            display: t('transactionDetails.currency'),
            value: transaction.Currency,
            visible: true,
          },
          {
            display: t('transactionDetails.clientReferenceNumber'),
            value: transaction.ClientReferenceNumber,
            visible: !!transaction.ClientReferenceNumber,
          },
          {
            display: t('transactionDetails.notes'),
            value: transactionHistory.map((data) => data.Notes).pop() || '',
            visible: !!transactionHistory.map((data) => data.Notes).pop(),
          },
        ];

        const transactionDetailsProps: vp.TableProps = {
          name: 'transaction-details',
          variant: 'pair',
          columns: {
            detailKey: { header: t('transactionDetails.header') || '' },
            detailValue: { header: '' },
          },
          rows: transactionDetailsPairs
            .filter(({ visible }) => visible)
            .map(({ display, value }) => ({
              detailKey: display,
              detailValue: value,
            })),
        };

        return transactionDetailsProps;
      },
      accountHolderDetails: () => {
        const accountHolderDetailsPairs: KeyValuePair[] = [
          {
            display: t('accountHolderDetails.name'),
            value: `${transaction.FirstName} ${transaction.LastName}`,
            visible: !!transaction.FirstName,
          },
          {
            display: t('accountHolderDetails.companyName'),
            value: transaction.CompanyName,
            visible: !!transaction.CompanyName,
          },
          {
            display: t('accountHolderDetails.address'),
            value: `${transaction.Address1} ${transaction.Address2}`,
            visible: transaction.Address1 !== '',
          },
          {
            display: t('accountHolderDetails.city'),
            value: transaction.City,
            visible: transaction.City !== '',
          },
          {
            display: t('accountHolderDetails.postalCode'),
            value: transaction.PostalCode,
            visible: !!transaction.PostalCode,
          },
          {
            display: t('accountHolderDetails.country'),
            value: transaction.Country,
            visible: true,
          },
          {
            display: t('accountHolderDetails.primaryContact'),
            value: transaction.PhoneNumber,
            visible: transaction.PhoneNumber !== '',
          },
        ];

        const accountHolderDetailsProps: vp.TableProps = {
          name: 'account-holder-details',
          variant: 'pair',
          columns: {
            detailKey: { header: t('accountHolderDetails.header') || '' },
            detailValue: { header: '' },
          },
          rows: accountHolderDetailsPairs
            .filter(({ visible }) => visible)
            .map(({ display, value }) => ({
              detailKey: display,
              detailValue: value,
            })),
        };

        return accountHolderDetailsProps;
      },
      bankAccountDetails: () => {
        const bankingInformationPairs: KeyValuePair[] = [
          {
            display: t('bankAccountDetails.financialInstitutionNumber'),
            value: transaction.FinancialInstitutionNumber,
          },
          {
            display: t('bankAccountDetails.branchTransitNumber'),
            value: transaction.BranchTransitNumber,
          },
          {
            display: t('bankAccountDetails.accountNumber'),
            value: transaction.AccountNumber,
          },
        ];

        const bankingInformationProps: vp.TableProps = {
          name: 'banking-information',
          variant: 'pair',
          columns: {
            detailKey: { header: t('bankAccountDetails.header') || '' },
            detailValue: { header: '' },
          },
          rows: bankingInformationPairs.map(({ display, value }) => ({
            detailKey: display,
            detailValue: value,
          })),
        };

        return bankingInformationProps;
      },
      errorInfo: () => {
        const ErrorInfoPairs: KeyValuePair[] = [
          {
            display: t('errorInfo.failureDateTime'),
            value: transaction.LastModified,
          },
          {
            display: t('errorInfo.amount'),
            value: `$${formattedCurrency(transaction.Amount || '')}`,
          },
          {
            display: t('errorInfo.failureReason'),
            value: state.failureReason,
          },
        ];

        const ErrorInfoProps: vp.TableProps = {
          name: 'error-info',
          variant: 'pair',
          columns: {
            detailKey: { header: t('errorInfo.header') || '' },
            detailValue: { header: '' },
          },
          rows: ErrorInfoPairs.map(({ display, value }) => ({
            detailKey: display,
            detailValue: value,
          })),
        };

        return ErrorInfoProps;
      },
    };

    return handlers[handler]();
  }
};

export default RefundTransactionInfo;
